import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth';
import { UserService } from '../services/user.service';
import { MessageService } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private authService: AuthService, 
    private router: Router,
    private userService: UserService,
    private messageService: MessageService
  ) {}

  canActivate():
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    console.log('AuthGuard: Checking authentication...');
    
    // Check if user is authenticated
    if (!this.authService.isAuthenticated()) {
      console.log('AuthGuard: User not authenticated, redirecting to login');
      this.router.navigate(['/']);
      return false;
    }

    // Get current user synchronously for immediate check
    const currentUser = this.authService.getLoggedInUser();
    console.log("AuthGuard: Current user", currentUser);
    
    if (!currentUser) {
      console.log('AuthGuard: No current user found, redirecting to login');
      this.router.navigate(['/']);
      return false;
    }

    // SUPER_DEV_ADMIN bypasses status checks
    if (currentUser?.role?.identification === 'SUPER_DEV_ADMIN') {
      console.log('AuthGuard: SUPER_DEV_ADMIN bypassing status checks');
      return true;
    }

    // Check user status
    if (currentUser?.status !== 'APPROVED') {
      console.log('AuthGuard: User status not approved:', currentUser?.status);
      
      // Allow PENDING users who are in first login to access account setup
      if (currentUser?.status === 'PENDING' && currentUser?.isFirstLogin) {
        console.log('AuthGuard: PENDING user in first login - allowing access to account setup');
        return true;
      } else if (currentUser?.status === 'PENDING') {
        this.router.navigate(['/account-pending-approval']);
      } else {
        this.router.navigate(['/']);
      }
      return false;
    }

    // For approved users (SA, A, FSR), check subscription status
    const userRole = currentUser?.role?.identification;
    const needsSubscriptionCheck = userRole === 'SA' || userRole === 'A' || userRole === 'FSR';
    
    if (needsSubscriptionCheck) {
      console.log('AuthGuard: Checking subscription status for', userRole);
      
      return this.userService.checkSubscriptionStatus().pipe(
        map((subscriptionStatus) => {
          console.log('AuthGuard: Subscription status:', subscriptionStatus);
          
          if (!subscriptionStatus.isValid) {
            if (subscriptionStatus.requiresPayment) {
              // SA user needs to pay
              // this.messageService.add({
              //   severity: 'warn',
              //   summary: 'Subscription Required',
              //   detail: subscriptionStatus.message,
              //   life: 5000
              // });
              this.router.navigate(['/payment/sub']);
              return false;
            } else {
              // A/FSR user - organization subscription is inactive
              // this.messageService.add({
              //   severity: 'error',
              //   summary: 'Subscription Inactive',
              //   detail: subscriptionStatus.message,
              //   life: 7000
              // });
              this.authService.logout();
              this.router.navigate(['/']);
              return false;
            }
          }
          
          console.log('AuthGuard: Subscription is active');
          return true;
        }),
        catchError((error) => {
          console.error('AuthGuard: Error checking subscription status', error);
          // In case of error, allow access but log the error
          return of(true);
        })
      );
    }

    console.log('AuthGuard: Authentication successful');
    return true;
  }
}
